import { LOCALE_MAP } from "@/const/timeZones";

function detectDateFormat(locale: Intl.LocalesArgument) {
  const testDate = new Date(2024, 11, 25); // Dec 25, 2024
  const formatter = new Intl.DateTimeFormat(locale);
  const formatted = formatter.format(testDate);
  
  const separator = formatted.match(/[\/\-\.\s]/)?.[0] || '/';
  
  const dayPos = formatted.indexOf('25');
  const monthPos = formatted.indexOf('12');
  const yearPos = formatted.indexOf('2024') >= 0 ? formatted.indexOf('2024') : formatted.indexOf('24');
  
  const hasFullYear = formatted.indexOf('2024') >= 0;
  const yearFormat = hasFullYear ? 'yyyy' : 'yy';
  
  const positions = [
    { pos: dayPos, format: 'dd' },
    { pos: monthPos, format: 'MM' },
    { pos: yearPos, format: yearFormat }
  ].sort((a, b) => a.pos - b.pos);
  
  return positions.map(p => p.format).join(separator);
}

/**
 * Detects the time format pattern from the user's locale
 * @param {string} locale - Browser locale (e.g., 'en-US')
 * @returns {string} date-fns format string
 */
function detectTimeFormat(locale: Intl.LocalesArgument) {
    const testTime = new Date(2024, 0, 1, 14, 30, 45); // 2:30:45 PM
    const formatter = new Intl.DateTimeFormat(locale, { 
        hour: 'numeric', 
        minute: 'numeric',
        second: 'numeric'
    });
    const formatted = formatter.format(testTime);
    
    const is12Hour = /AM|PM|am|pm|a\.m\.|p\.m\./i.test(formatted);
    
    const hasLeadingZero = /^0\d|[\s:]0\d/.test(formatted);
    const hourFormat = hasLeadingZero ? 'HH' : 'H';
    const hour12Format = hasLeadingZero ? 'hh' : 'h';
    
    if (is12Hour) {
        if (formatted.includes('a.m.') || formatted.includes('p.m.')) {
            return `${hour12Format}:mm a`;
        } else if (formatted.includes('am') || formatted.includes('pm')) {
            return `${hour12Format}:mm aa`;
        } else {
            return `${hour12Format}:mm a`;
        }
    } else {
        return `${hourFormat}:mm`;
    }
}

/**
 * Gets the date-fns locale identifier from browser locale
 * @param {string} browserLocale - Browser locale (e.g., 'en-US')
 * @returns {string} date-fns locale identifier (e.g., 'enUS')
 */
function getDateFnsLocale(browserLocale: string) {
  // Try exact match first
  if (browserLocale in LOCALE_MAP) {
    return LOCALE_MAP[browserLocale as keyof typeof LOCALE_MAP];
  }
  
  // Try language code only (e.g., 'en' from 'en-US')
  const langCode = browserLocale.split('-')[0];
  const fallback = Object.keys(LOCALE_MAP).find(key => 
    key.startsWith(langCode + '-')
  );
  
  return fallback ? LOCALE_MAP[fallback as keyof typeof LOCALE_MAP] : 'enUS'; // Default to enUS
}

/**
 * Main helper function to get locale settings
 * @returns {Object} { locale, dateFormat, timeFormat, timezone, dateFnsLocale }
 */
export function getLocaleSettings() {
  const browserLocale = navigator.language || navigator.languages?.[0] || 'en-US';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  return {
    locale: browserLocale,
    dateFormat: detectDateFormat(browserLocale),
    timeFormat: detectTimeFormat(browserLocale),
    timezone: timezone, 
    dateFnsLocale: getDateFnsLocale(browserLocale)
  };
}